import { create } from 'zustand';
import type { QuestPanelKey, QuestPanelState, QuestPanelStore } from './questPanelTypes';

export type {
  QuestPanelActions,
  QuestPanelKey,
  QuestPanelState,
  QuestPanelStore,
  QuestScope,
} from './questPanelTypes';

/** 카드 선택 해제 + 우측 패널을 dashboard 상태로 되돌리는 patch (clearInternalSelection 와 동일 범위).
 * 패널별 칸만 비운다 — 반대편 창(동시 마운트)의 상세를 함께 닫지 않기 위함. */
const clearQuestSelectionPatch = (state: QuestPanelState, panelKey: QuestPanelKey) =>
  ({
    internalSelectedIdByPanel: { ...state.internalSelectedIdByPanel, [panelKey]: undefined },
    mobileShowDetailByPanel: { ...state.mobileShowDetailByPanel, [panelKey]: false },
    selectedProofId: undefined,
    proofModalOpen: false,
  }) as const;

/** 보관 브라우즈 모드 종료 patch — "스파인 내비게이션 = 모드 탈출" 규칙의 단일 지점.
 * 스파인 탭 선택 액션들이 공통으로 spread 한다. 새 스파인 내비게이션 액션도 이걸 spread 하면
 * 모드 탈출 규칙을 자동 상속(각 액션에 두 줄씩 복붙하다 하나 빠뜨리는 실수 방지). */
const exitArchiveBrowsePatch = {
  showArchivedScenarios: false,
  archivedScenarioSelectedId: null,
} as const;

const initialState: QuestPanelState = {
  selectedQuestId: null,
  selectedWorldQuestId: null,
  questScope: 'TEAM',
  selectedScenarioId: null,
  scenarioBriefPinned: false,
  unclassifiedTabSelected: false,
  adcodeTabSelected: false,
  selectedProductId: null,
  productUnclassifiedSelected: false,
  productManageOpen: false,
  internalSelectedIdByPanel: { TEAM: undefined, GLOBAL: undefined, TEAM_PRODUCT: undefined },
  viewMode: 'my-quests',
  questSearch: '',
  showArchived: false,
  showArchivedScenarios: false,
  archivedScenarioSelectedId: null,
  mode: 'viewing',
  selectedProofId: undefined,
  mobileShowDetailByPanel: { TEAM: false, GLOBAL: false, TEAM_PRODUCT: false },
  detailFormOpenByPanel: { TEAM: false, GLOBAL: false, TEAM_PRODUCT: false },
  questFocusRequestId: null,
  questFocusNonce: 0,
  questCardFocusRequestId: null,
  proofModalOpen: false,
  acceptModalOpen: false,
  rejectModalOpen: false,
  rejectingProofId: undefined,
  questRejectModalOpen: false,
  proofDetailModalOpen: false,
  proofDetailTargetId: undefined,
};

export const useQuestPanelStore = create<QuestPanelStore>()((set) => ({
  ...initialState,

  setSelectedQuestId: (id) => {
    set({ selectedQuestId: id });
  },

  setSelectedWorldQuestId: (id) => {
    set({ selectedWorldQuestId: id });
  },

  setQuestScope: (scope) => {
    set({ questScope: scope });
  },

  setSelectedScenarioId: (id) => {
    set({ selectedScenarioId: id, scenarioBriefPinned: false });
  },

  // 시나리오 스파인은 TEAM scope 전용 — selectedWorldQuestId(GLOBAL scope)는 보존하여
  // 월드 퀘스트 ↔ 퀘스트 페이지 왕복 시 월드 quest 선택 상태가 사라지지 않게 함 (scope isolation).
  // 스파인 내비게이션(시나리오/미분류/애드코드 탭)은 보관 브라우즈 모드를 자동 종료한다 —
  // 딥링크(useQuestDeepLink → selectScenario) 진입 시에도 보관 목록이 우측을 가리지 않게.
  // 스파인은 TEAM 전용(enableSpine=false 인 월드 창엔 없음)이라 선택 해제도 TEAM 칸만 건드린다.
  selectScenario: (id, panelKey = 'TEAM', pinBrief = false) => {
    set((state) => ({
      selectedScenarioId: id,
      scenarioBriefPinned: pinBrief,
      unclassifiedTabSelected: false,
      adcodeTabSelected: false,
      selectedQuestId: null,
      ...exitArchiveBrowsePatch,
      ...clearQuestSelectionPatch(state, panelKey),
    }));
  },

  selectUnclassifiedTab: (panelKey = 'TEAM') => {
    set((state) => ({
      scenarioBriefPinned: false,
      selectedScenarioId: null,
      unclassifiedTabSelected: true,
      adcodeTabSelected: false,
      selectedQuestId: null,
      ...exitArchiveBrowsePatch,
      ...clearQuestSelectionPatch(state, panelKey),
    }));
  },

  selectAdcodeTab: (panelKey = 'TEAM') => {
    set((state) => ({
      scenarioBriefPinned: false,
      selectedScenarioId: null,
      unclassifiedTabSelected: false,
      adcodeTabSelected: true,
      selectedQuestId: null,
      ...exitArchiveBrowsePatch,
      ...clearQuestSelectionPatch(state, panelKey),
    }));
  },

  // 보관 브라우즈도 스파인(TEAM) 전용 진입점 — archiveBrowseMode 가 enableSpine 에 묶여 있다.
  selectArchivedScenario: (id) => {
    set((state) => ({
      archivedScenarioSelectedId: id,
      mobileShowDetailByPanel: { ...state.mobileShowDetailByPanel, TEAM: true },
    }));
  },

  resetSelection: () => {
    set({
      selectedQuestId: null,
      selectedWorldQuestId: null,
      selectedScenarioId: null,
      unclassifiedTabSelected: false,
      adcodeTabSelected: false,
      // 다른 선택-초기화 액션과 동일하게 보관 모드도 함께 종료(archivedScenarioSelectedId 만 지우고
      // showArchivedScenarios 를 남기면 auto-select 가드에 걸려 스파인이 폴백 선택을 못 하는 stuck 상태).
      ...exitArchiveBrowsePatch,
      internalSelectedIdByPanel: { TEAM: undefined, GLOBAL: undefined, TEAM_PRODUCT: undefined },
      mobileShowDetailByPanel: { TEAM: false, GLOBAL: false, TEAM_PRODUCT: false },
      selectedProofId: undefined,
      proofModalOpen: false,
    });
  },

  clearQuestSelectionKeepScenario: (panelKey) => {
    set((state) => ({
      ...(panelKey === 'GLOBAL' ? { selectedWorldQuestId: null } : { selectedQuestId: null }),
      ...clearQuestSelectionPatch(state, panelKey),
    }));
  },

  // 다음 건이 있으면 이어받고, 없으면 clearQuestSelectionKeepScenario 와 같은 자리로 떨어진다
  // (시나리오 선택은 두 경우 모두 보존 — 정리하던 시나리오에서 튕겨 나가지 않게).
  // clearQuestSelectionPatch 를 먼저 편 뒤 폴백 칸을 덮어쓴다: 모바일 상세 닫힘·증빙 선택
  // 초기화 같은 나머지 규칙을 이 액션이 따로 복제하지 않기 위함.
  selectQuestAfterArchive: (panelKey, nextId) => {
    set((state) => ({
      ...(panelKey === 'GLOBAL'
        ? { selectedWorldQuestId: nextId ?? null }
        : { selectedQuestId: nextId ?? null }),
      ...clearQuestSelectionPatch(state, panelKey),
      internalSelectedIdByPanel: { ...state.internalSelectedIdByPanel, [panelKey]: nextId },
      questCardFocusRequestId: nextId ?? null,
    }));
  },

  // 제품 축(개발 퀘스트 창). 시나리오 축 액션과 같은 모양이되 건드리는 칸이 다르다 —
  // selectedScenarioId·unclassifiedTabSelected 를 손대면 옆에 열린 팀 퀘스트 창이 함께 움직인다.
  selectProduct: (id) => {
    set((state) => ({
      selectedProductId: id,
      productUnclassifiedSelected: false,
      selectedQuestId: null,
      ...clearQuestSelectionPatch(state, 'TEAM_PRODUCT'),
    }));
  },

  selectProductUnclassifiedTab: () => {
    set((state) => ({
      selectedProductId: null,
      productUnclassifiedSelected: true,
      selectedQuestId: null,
      ...clearQuestSelectionPatch(state, 'TEAM_PRODUCT'),
    }));
  },

  // 우측 상세 영역은 퀘스트 상세·생성·편집과 자리를 나눠 쓴다. 열 때 생성/편집 모드를 접어야
  // 두 화면이 겹치지 않는다(닫을 때는 되돌리지 않는다 — 사용자가 명시적으로 연 것이 아니다).
  setProductManageOpen: (open) => {
    set((state) => ({
      productManageOpen: open,
      ...(open
        ? { mode: 'viewing' as const, ...clearQuestSelectionPatch(state, 'TEAM_PRODUCT') }
        : {}),
    }));
  },

  consumeQuestCardFocusRequest: () => {
    set({ questCardFocusRequestId: null });
  },

  setInternalSelectedId: (panelKey, id) => {
    set((state) => ({
      internalSelectedIdByPanel: { ...state.internalSelectedIdByPanel, [panelKey]: id },
    }));
  },

  clearInternalSelection: (panelKey) => {
    set((state) => ({
      ...clearQuestSelectionPatch(state, panelKey),
    }));
  },

  setViewMode: (mode) => {
    set({ viewMode: mode });
  },

  setQuestSearch: (search) => {
    set({ questSearch: search });
  },

  setShowArchived: (showArchived) => {
    set((state) => ({
      showArchived:
        typeof showArchived === 'function' ? showArchived(state.showArchived) : showArchived,
    }));
  },

  setMode: (mode) => {
    set({ mode });
  },

  setSelectedProofId: (proofId) => {
    set({ selectedProofId: proofId });
  },

  clearSelectedProofId: () => {
    set({ selectedProofId: undefined });
  },

  setProofModalOpen: (open) => {
    set({ proofModalOpen: open });
  },

  setAcceptModalOpen: (open) => {
    set({ acceptModalOpen: open });
  },

  setRejectModalOpen: (open) => {
    set({ rejectModalOpen: open });
  },

  setRejectingProofId: (proofId) => {
    set({ rejectingProofId: proofId });
  },

  setQuestRejectModalOpen: (open) => {
    set({ questRejectModalOpen: open });
  },

  setMobileShowDetail: (panelKey, open) => {
    set((state) => ({
      mobileShowDetailByPanel: {
        ...state.mobileShowDetailByPanel,
        [panelKey]:
          typeof open === 'function' ? open(state.mobileShowDetailByPanel[panelKey]) : open,
      },
    }));
  },

  setDetailFormOpen: (panelKey, open) => {
    set((state) =>
      state.detailFormOpenByPanel[panelKey] === open
        ? state
        : { detailFormOpenByPanel: { ...state.detailFormOpenByPanel, [panelKey]: open } }
    );
  },

  requestQuestFocus: (id) => {
    set((state) => ({ questFocusRequestId: id, questFocusNonce: state.questFocusNonce + 1 }));
  },

  // 소비는 "필터를 대상에 맞췄다"는 뜻이지 "스크롤까지 끝났다"가 아니다 — nonce 를 같이
  // 되돌리면 뒤늦게 렌더되는 목록이 그 착지를 못 알아본다.
  consumeQuestFocusRequest: () => {
    set({ questFocusRequestId: null });
  },

  toggleShowArchived: () => {
    set((state) => ({
      showArchived: !state.showArchived,
    }));
  },

  // 모드 종료(OFF)는 곧 보관 선택 소멸(exitArchiveBrowsePatch) — 다음 진입은 항상 목록부터.
  // ON 은 플래그만 켠다. 두 액션이 같은 규칙을 쓰도록 setShowArchivedScenarios 로 위임.
  toggleArchivedScenarios: () => {
    set((state) =>
      state.showArchivedScenarios ? exitArchiveBrowsePatch : { showArchivedScenarios: true }
    );
  },

  setShowArchivedScenarios: (v) => {
    set(v ? { showArchivedScenarios: true } : exitArchiveBrowsePatch);
  },

  openProofDetail: (proofId) => {
    set({ proofDetailModalOpen: true, proofDetailTargetId: proofId });
  },

  closeProofDetail: () => {
    set({ proofDetailModalOpen: false, proofDetailTargetId: undefined });
  },

  resetQuestPanelState: () => {
    set(initialState);
  },

  resetMobileOnlyState: () => {
    set({
      mobileShowDetailByPanel: { TEAM: false, GLOBAL: false, TEAM_PRODUCT: false },
      proofModalOpen: false,
      acceptModalOpen: false,
      rejectModalOpen: false,
      questRejectModalOpen: false,
      selectedProofId: undefined,
      rejectingProofId: undefined,
      proofDetailModalOpen: false,
      proofDetailTargetId: undefined,
    });
  },
}));

export const selectQuestPanelData = (state: QuestPanelStore): QuestPanelState => ({
  selectedQuestId: state.selectedQuestId,
  selectedWorldQuestId: state.selectedWorldQuestId,
  questScope: state.questScope,
  selectedScenarioId: state.selectedScenarioId,
  scenarioBriefPinned: state.scenarioBriefPinned,
  unclassifiedTabSelected: state.unclassifiedTabSelected,
  selectedProductId: state.selectedProductId,
  productUnclassifiedSelected: state.productUnclassifiedSelected,
  productManageOpen: state.productManageOpen,
  adcodeTabSelected: state.adcodeTabSelected,
  internalSelectedIdByPanel: state.internalSelectedIdByPanel,
  viewMode: state.viewMode,
  questSearch: state.questSearch,
  showArchived: state.showArchived,
  showArchivedScenarios: state.showArchivedScenarios,
  archivedScenarioSelectedId: state.archivedScenarioSelectedId,
  mode: state.mode,
  selectedProofId: state.selectedProofId,
  mobileShowDetailByPanel: state.mobileShowDetailByPanel,
  detailFormOpenByPanel: state.detailFormOpenByPanel,
  questFocusRequestId: state.questFocusRequestId,
  questFocusNonce: state.questFocusNonce,
  questCardFocusRequestId: state.questCardFocusRequestId,
  proofModalOpen: state.proofModalOpen,
  acceptModalOpen: state.acceptModalOpen,
  rejectModalOpen: state.rejectModalOpen,
  rejectingProofId: state.rejectingProofId,
  questRejectModalOpen: state.questRejectModalOpen,
  proofDetailModalOpen: state.proofDetailModalOpen,
  proofDetailTargetId: state.proofDetailTargetId,
});

export const selectQuestPanelUiState = selectQuestPanelData;
